import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { en, de } from 'yup-locales';
import * as Sentry from "@sentry/react";
import { api } from "helpers/axios";
import { errorMessage } from "helpers/common";
import { toast } from "helpers/toast";
import { getLang, getModule } from "store/userSlice";
import { getAllCustomNames } from "store/fieldsSlice";
import { fetchBalanceScorecardList, getBalanceScorecardList } from "store/balanceScorecardSlice";
import { fetchBudgetSettings, getBudgetSettings } from "store/budgetSettingsSlice";
import { fetchGroups, getGroups } from "store/groupsSlice";
import { useTranslation } from "translation/useTranslation";
import ErrorAlert from "components/ui/ErrorAlert";
import Link from "components/ui/Link";
import EmployeeSelector from "./EmployeeSelector";
import ListPagination from "components/ui/ListPagination";
import ActionChangePreview from "./ActionChangePreview";
import { createFormKeys, requiredCreateFormKeys, instanceComplete, buildOverwritePayload, buildInstanceUpdatesPayload } from "./createBalancedScorecardHelpers";
import { applySelectionChange, buildWhoList, pairInstanceValues } from "./actionChangePreviewHelpers";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { isDbModule } from "helpers/module";
import { Button, Field, Input, InputAffix, NativeSelect, Select } from "components/ui";
import useEmployeePool from "./useEmployeePool";
import { employeeManagementPath } from "helpers/employeeDeeplink";
import sanitize from "helpers/sanitize";

const NORMAL_RATING_LOCKED = 'normal_rating_locked';
const NORMAL_RATING = 'normal_rating';

export default function CreateBalancedScorecardAction({ action, filters, page, setPage, filterCount, setFilterCount, tabs, activeTab, onTabChange, onBack, asSubPage = false, sharedSelection, sharedPool }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const lang = useSelector(getLang);

  const { translate, editableTranslate, editableTranslateString } = useTranslation();

  const currentModule = useSelector(getModule);
  const isDbModuleActive = useMemo(() => isDbModule(currentModule), [currentModule]);

  const fieldCustomNames = useSelector(getAllCustomNames);
  const balancedScorecardList = useSelector(getBalanceScorecardList);
  const budgets = useSelector(getBudgetSettings);
  const groups = useSelector(getGroups);

  const [actionType, setActionType] = useState('create_balanced_scorecard');
  const [localActionMode, setLocalActionMode] = useState('overwrite'); // 'overwrite' | 'per_instance'
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState('danger');
  const [localSelected, setLocalSelected] = useState([]); // overwrite: employee hash_ids
  const [localSelectedEmployees, setLocalSelectedEmployees] = useState({}); // overwrite: hash_id -> display data (step-2 who list)
  // Embedded in FillFieldsAction: the parent owns mode + overwrite selection so they survive tab switches.
  const { actionMode, setActionMode, selected, setSelected, selectedEmployees, setSelectedEmployees } = sharedSelection || {
    actionMode: localActionMode, setActionMode: setLocalActionMode,
    selected: localSelected, setSelected: setLocalSelected,
    selectedEmployees: localSelectedEmployees, setSelectedEmployees: setLocalSelectedEmployees,
  };
  const [checkedEmployees, setCheckedEmployees] = useState({}); // per-instance: hash_id -> display data (step-2 who list)
  const [instanceChecked, setInstanceChecked] = useState([]); // per-instance: checked hash_ids
  const [instanceValues, setInstanceValues] = useState({}); // per-instance: { [hash_id]: { [key]: value } }
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [poolPage, setPoolPage] = useState(1);
  const [poolPerPage, setPoolPerPage] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const [poolSearch, setPoolSearch] = useState('');

  // Switching overwrite <-> per-instance starts the employee list fresh (page 1, no search).
  const changeActionMode = (mode) => {
    if (mode === actionMode) return;
    setActionMode(mode);
    setSearchInput('');
    setPoolSearch('');
    setPoolPage(1);
  };

  // Fetch supporting lists once.
  useEffect(() => {
    if (!balancedScorecardList) dispatch(fetchBalanceScorecardList());
    if (!isDbModuleActive) {
      if (!budgets) dispatch(fetchBudgetSettings());
      if (!groups) dispatch(fetchGroups());
    }
  }, [dispatch]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPoolSearch(searchInput.trim());
      setPoolPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPoolPage(1);
  }, [filters, action]);

  // Embedded overwrite: the parent already fetched this pool.
  const { employees: localEmployees, pagination, loading: localLoadingPool, loaded: localPoolLoaded, error: poolError } = useEmployeePool({
    filters,
    page: poolPage,
    perPage: poolPerPage,
    search: poolSearch,
    enabled: !(sharedPool && actionMode === 'overwrite'),
    // Report the matched count to the detail header (system-filter context).
    onTotal: setFilterCount,
  });

  useEffect(() => {
    if (!poolError) return;
    setErrorType('danger');
    setError(poolError);
  }, [poolError]);

  const handlePoolPerPageChange = (n) => {
    setPoolPerPage(n);
    setPoolPage(1);
  };

  const localPoolPaginationProps = {
    search: searchInput,
    onSearchChange: setSearchInput,
    total: pagination.total,
    loadingPool: localLoadingPool,
    page: pagination.currentPage,
    perPage: pagination.perPage,
    totalPages: pagination.totalPages,
    totalRows: pagination.total,
    setPage: setPoolPage,
    setPerPage: handlePoolPerPageChange,
  };

  // Embedded overwrite: reuse FillFieldsAction's selector pool (list, page, search) so tab switches don't refetch.
  // Per-instance keeps its own pool.
  const { employees, loadingPool, poolLoaded, poolPaginationProps } = (sharedPool && actionMode === 'overwrite') ? sharedPool : {
    employees: localEmployees, loadingPool: localLoadingPool, poolLoaded: localPoolLoaded, poolPaginationProps: localPoolPaginationProps,
  };

  // The action only creates first scorecards (backend skips employees who already have one),
  // so reference mode is available whenever LOB is active, independent of the filter.
  const canUseReferenceMode = !isDbModuleActive;

  // ---- option lists ----

  const balancedScorecardListFiltered = useMemo(() => {
    if (!balancedScorecardList) return [];
    return balancedScorecardList.filter(bs => isDbModuleActive ? bs?.form_definition?.type === 'custom_balanced_scorecard_setting' : bs?.form_definition?.type !== 'custom_balanced_scorecard_setting');
  }, [balancedScorecardList, isDbModuleActive]);

  const templateGroupOptions = useMemo(() => {
    if (!balancedScorecardList) return [];
    const groupsMap = new Map();
    balancedScorecardList.forEach((bs) => {
      const group = bs?.balanced_scorecard_setting_group;
      if (group?.hash_id && !groupsMap.has(group.hash_id)) {
        groupsMap.set(group.hash_id, { name: group.name, hash_id: group.hash_id });
      }
    });
    return Array.from(groupsMap.values());
  }, [balancedScorecardList]);

  const templateOptionsForGroup = (groupHashId) => {
    if (!balancedScorecardList || !groupHashId) return [];
    return balancedScorecardList
      .filter(bs => bs?.balanced_scorecard_setting_group?.hash_id === groupHashId)
      .map(bs => {
        const label = bs?.balanced_scorecard_setting_group?.name
          ? bs.slug.replace(new RegExp(`^${bs.balanced_scorecard_setting_group.name}\\s*-\\s*`, 'i'), '')
          : bs.slug;
        return {
          name: translate('state_employee.rating_mode.template_' + label?.toLowerCase(), label),
          hash_id: bs.hash_id,
        };
      });
  };

  const fieldLabels = {
    rating_mode: translate('field.rating_mode', 'Bewertungsmodus'),
    balanced_scorecard_template_group: fieldCustomNames?.['field.balanced_scorecard_template'] || translate('state_employee.rating_mode'),
    balanced_scorecard_template: isDbModuleActive
      ? translate('state_employee.rating_mode.template')
      : (fieldCustomNames?.['field.balanced_scorecard_template'] || translate('field.balanced_scorecard_template')),
    budget: fieldCustomNames?.['field.budget'] || translate('field.budget'),
    group: fieldCustomNames?.['field.group'] || translate('field.group'),
  };

  const formKeys = createFormKeys(isDbModuleActive);
  const requiredFormKeys = requiredCreateFormKeys(isDbModuleActive);

  const selectOptionsForKey = (key, values = null) => optionsForKey(key, values).map(item => ({ value: item.hash_id, label: item.name }));

  const optionsForKey = (key, values = null) => {
    if (key === 'balanced_scorecard_template') {
      return isDbModuleActive ? templateOptionsForGroup(values?.balanced_scorecard_template_group ?? watchTemplateGroup) : balancedScorecardListFiltered;
    }
    if (key === 'balanced_scorecard_template_group') return templateGroupOptions;
    if (key === 'budget') return budgets || [];
    if (key === 'group') return groups || [];
    return [];
  };

  // ---- overwrite form (RHF + yup) ----

  const schema = useMemo(() => {
    return yup.object({
      rating_mode: yup.string().notRequired().label(translate('field.rating_mode', 'Bewertungsmodus')),
      balanced_scorecard_template_group: isDbModuleActive
        ? yup.string().required().label(fieldCustomNames?.['field.balanced_scorecard_template'] || translate('state_employee.rating_mode'))
        : yup.string().notRequired(),
      balanced_scorecard_template: yup.string().when('rating_mode', {
        is: (rating_mode) => rating_mode === NORMAL_RATING_LOCKED && canUseReferenceMode,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required(),
      }).label(translate(isDbModuleActive ? 'state_employee.rating_mode.template' : 'field.balanced_scorecard_template')),
      budget: yup.string().notRequired().label(fieldCustomNames?.['field.budget'] || translate('field.budget')),
      group: yup.string().notRequired().label(fieldCustomNames?.['field.group'] || translate('field.group')),
    });
  }, [canUseReferenceMode]);

  const { register, control, trigger, getValues, setValue, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: canUseReferenceMode ? { rating_mode: NORMAL_RATING_LOCKED } : {},
  });

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  const watchTemplateGroup = watch('balanced_scorecard_template_group');
  const watchRatingMode = watch('rating_mode');

  // Reset the template when the rating-mode group changes.
  useEffect(() => {
    if (isDbModuleActive) setValue('balanced_scorecard_template', '');
  }, [watchTemplateGroup]);

  // Reference mode resolves template, budget and group per employee, so drop any manual picks.
  useEffect(() => {
    if (watchRatingMode === NORMAL_RATING_LOCKED) formKeys.forEach(key => setValue(key, ''));
  }, [watchRatingMode]);

  // ---- per-instance state ------------------------------------------------------------------------

  const toggleInstanceRow = (hashId, isChecked) => {
    setInstanceChecked(prev => {
      if (isChecked) return prev.includes(hashId) ? prev : [...prev, hashId];
      return prev.filter(id => id !== hashId);
    });
    // Keep display data for the who list across pagination.
    const employee = (employees || []).find(e => e.hash_id === hashId) || { hash_id: hashId };
    setCheckedEmployees(prev => applySelectionChange(prev, [employee], isChecked));

    if (canUseReferenceMode) {
      setInstanceValue(hashId, 'rating_mode', NORMAL_RATING_LOCKED);
    }
  };

  const setInstanceValue = (hashId, key, value) => {
    setInstanceValues(prev => {
      const cur = { ...(prev[hashId] || {}), [key]: value };
      // Reset the template when the group changes.
      if (key === 'balanced_scorecard_template_group') cur.balanced_scorecard_template = '';
      if (key === 'rating_mode' && value === NORMAL_RATING_LOCKED) formKeys.forEach(k => { cur[k] = ''; });
      return { ...prev, [hashId]: cur };
    });
  };

  // ---- step transitions / apply -------------------------------------------------------------------

  const goToPreview = async () => {
    setError(null);
    setErrorType('danger');

    if (actionMode === 'overwrite') {
      if (selected.length < 1) {
        setError(editableTranslateString('batch_update.checked_employees_required'));
        return;
      }
      if (!(await trigger(formKeys))) return;
    } else {
      if (instanceChecked.length < 1) {
        setError(editableTranslateString('batch_update.checked_employees_required'));
        return;
      }

      if (canUseReferenceMode) {
        for (const hashId of instanceChecked) {
          if (instanceValues?.[hashId]?.rating_mode === NORMAL_RATING && !instanceComplete(requiredFormKeys, instanceValues[hashId])) {
            setError(editableTranslateString('action.detail.create_lk.instance.fill_required', 'Bitte füllen Sie für jeden ausgewählten Beschäftigte*n alle Felder aus.'));
            return;
          }
        }
      } else if (!instanceChecked.every(hash => instanceComplete(requiredFormKeys, instanceValues[hash]))) {
        setError(editableTranslateString('action.detail.create_lk.instance.fill_required', 'Bitte füllen Sie für jeden ausgewählten Beschäftigte*n alle Felder aus.'));
        return;
      }
    }

    setPreview(null);
    setPage(2);
  };

  const goToApply = async () => {
    if (await fetchPreview()) setPage(3);
  };

  const fetchPreview = async () => {
    setLoadingPreview(true);
    setError(null);
    setErrorType('danger');

    try {
      const { data, status } = await api.post('/employee-balanced-scorecards/bulk-create/preview', buildPayload());
      if (status === 200) {
        setPreview(data?.data || null);
        return true;
      }
      return false;
    } catch (err) {
      setErrorType('danger');
      setError(errorMessage(err, 'pre-check create balanced scorecard'));
      Sentry.captureException(err);
      return false;
    } finally {
      setLoadingPreview(false);
    }
  };

  const buildPayload = () => (
    actionMode === 'overwrite'
      ? buildOverwritePayload({ filters, selected, values: getValues(), isDbModuleActive })
      : buildInstanceUpdatesPayload({ instanceChecked, instanceValues, isDbModuleActive })
  );

  const linkToEmployeeManagement = (employeeNumber, label = null) => {
    return `<a href="${employeeManagementPath({ staffId: employeeNumber })}" target="_blank" rel="noopener noreferrer">${label || employeeNumber}</a>`;
  };

  const buildSkippedWarning = (data) => {
    const texts = [];
    const skipped = data?.skipped_employees_max || [];
    const noGroup = data?.skipped_employees_no_group || [];
    const incompleteReference = data?.skipped_employees_incomplete_reference || [];
    const existingScorecard = data?.skipped_employees_existing_scorecard || [];

    if (skipped.length > 0) {
      const listHtml = skipped.map(e => `- ${linkToEmployeeManagement(e.employee_number, e.full_name)}`).join('<br />');
      if (isDbModuleActive) {
        const grouped = skipped.reduce((acc, e) => {
          const key = e?.message || translate('state_employee.batch_create_skipped_max_balanced_scorecard');
          (acc[key] = acc[key] || []).push(e);
          return acc;
        }, {});
        Object.entries(grouped).forEach(([message, list]) => {
          texts.push(message + '<br/>' + list.map(e => `- ${linkToEmployeeManagement(e.employee_number, e.full_name)}`).join('<br />'));
        });
      } else {
        texts.push(translate('batch_update.batch_create_skipped_max_balanced_scorecard') + '<br/>' + listHtml);
      }
    }
    if (isDbModuleActive && noGroup.length > 0) {
      const listHtml = noGroup.map(e => `- ${linkToEmployeeManagement(e.employee_number, e.full_name)}`).join('<br />');
      texts.push(translate('state_employee.batch_create_skipped_no_group') + '<br/>' + listHtml);
    }
    // Any mode: employee already has a scorecard, and the action only creates the first one.
    if (existingScorecard.length > 0) {
      const listHtml = existingScorecard.map(e => `- ${linkToEmployeeManagement(e.employee_number, e.full_name)}`).join('<br />');
      texts.push(translate('batch_update.batch_create_skipped_existing_scorecard') + '<br/>' + listHtml);
    }
    // Locked normal rating only: the required template, or an optional value
    // that was supplied, could not be resolved from the employee reference.
    // The backend names the entity type, the value and why it failed
    // (LIX-1768); the generic sentence only remains for an entry without that.
    if (incompleteReference.length > 0) {
      const specific = incompleteReference.filter(e => e.type && e.reason);
      const generic = incompleteReference.filter(e => !(e.type && e.reason));
      if (specific.length > 0) {
        const listHtml = specific.map(e => `- ${linkToEmployeeManagement(e.employee_number, e.full_name)}: ${describeUnresolvedReference(e)}`).join('<br />');
        texts.push(translate('batch_update.batch_create_skipped_unresolved_reference') + '<br/>' + listHtml);
      }
      if (generic.length > 0) {
        const listHtml = generic.map(e => `- ${linkToEmployeeManagement(e.employee_number, e.full_name)}`).join('<br />');
        texts.push(translate('batch_update.batch_create_skipped_incomplete_reference') + '<br/>' + listHtml);
      }
    }

    return texts.join('<br /><br />');
  };

  const escapeHtml = (text) => String(text ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // "Referenzbudget 'Budget 4 (Beschäftigte)' existiert nicht." from the
  // backend's type / reason / value tokens. The value is customer data.
  const describeUnresolvedReference = (e) => translate(`batch_update.unresolved_reference_${e.reason}`, null, {
    type: translate(`reference_type.${e.type}`),
    value: escapeHtml(e.value)
  });

  const handleApply = async () => {
    setError(null);
    setErrorType('danger');
    setSubmitting(true);

    try {
      const { data, status } = await api.post('/employee-balanced-scorecards/bulk-create', buildPayload());
      if (status !== 200) throw new Error('unexpected status');

      toast.success(translate('form.success.saved'));
      navigate('/settings/actions');
    } catch (err) {
      setErrorType('danger');
      setError(errorMessage(err, 'apply create balanced scorecard action'));
      Sentry.captureException(err);
      setSubmitting(false);
    }
  };

  // ---- render helpers -----------------------------------------------------------------------------

  const renderSelect = (key, err = null) => {
    const opts = selectOptionsForKey(key);
    return (
      <>
        <Controller
          name={key}
          control={control}
          render={({ field }) => (
            <div style={{ display: 'contents' }} data-testid={`create-scorecard-${key}-select`}>
              <Select
                inputId={`create-scorecard-${key}`}
                options={opts}
                value={opts.find(o => o.value === field.value) || null}
                onChange={option => field.onChange(option ? option.value : '')}
                onBlur={field.onBlur}
                isClearable
                placeholder={translate('form.select_placeholder')}
              />
            </div>
          )}
        />
        {err && <div className="peb-field__error">{err}</div>}
      </>
    );
  };

  const overwriteForm = useMemo(() => (
    <div>
      {
        canUseReferenceMode && <div className="row">
          <div className="col-xl-4 col-lg-6 form-group">
            <Field label={fieldLabels.rating_mode}>
              <NativeSelect {...register('rating_mode')} data-testid="rating-mode-select">
                <option value={NORMAL_RATING_LOCKED}>
                  {translate('field.rating_mode.normal_rating_locked', 'Normale Bewertung')}
                </option>
                <option value={NORMAL_RATING}>
                  {translate('field.rating_mode.normal_rating', 'Bewertung')}
                </option>
              </NativeSelect>
            </Field>
          </div>
        </div>
      }
      <div className="row">
        {formKeys.map((key) => (
          <div key={key} className="col-xl-4 col-lg-6 form-group">
            <Field
              label={fieldLabels[key]}
              htmlFor={`create-scorecard-${key}`}
              help={(canUseReferenceMode && watchRatingMode === NORMAL_RATING_LOCKED) && <i className="bi bi-info-circle ml-1" data-tooltip-id={`action-detail-first-${key}-tooltip`}></i>}
            >
              {!canUseReferenceMode || watchRatingMode !== NORMAL_RATING_LOCKED ? renderSelect(key, errors[key]?.message) : <Input data-testid={`create-scorecard-${key}-input`} id={`create-scorecard-${key}`} defaultValue={'...'} disabled={true} />}
            </Field>
          </div>
        ))}
        {
          isDbModuleActive && <div className="col-xl-4 col-lg-6 form-group">
            <Field label={fieldLabels['group']} htmlFor="create-scorecard-group" help={<i className="bi bi-info-circle ml-1" data-tooltip-id="action-detail-state-group-tooltip"></i>}>
              <Input data-testid="create-scorecard-group-input" id="create-scorecard-group" defaultValue={'...'} disabled={true} />
            </Field>
          </div>
        }
      </div>
    </div>
  ), [formKeys, canUseReferenceMode]);

  const renderInstanceEditor = () => (
    <div>
      <div className="mb-3">{editableTranslate('action.detail.fill_per_employee_text', 'Wählen Sie Beschäftigte aus und füllen Sie die Felder aus.')}</div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <InputAffix suffix={<i className="bi bi-search ml-2"></i>}>
          <Input data-testid="search-input"
            type="text"
            placeholder={translate('global.search', 'Suche')}
            maxLength={255}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
          />
        </InputAffix>
        <div className="text-muted text-nowrap ml-2">{instanceChecked.length} / {pagination.total}</div>
      </div>

      {
        loadingPool && (
          <div className="text-center p-2" data-testid="loading">
            <div className="spinner-border spinner-border-sm" role="status"><span className="sr-only">Wird geladen...</span></div>
          </div>
        )
      }

      {
        (employees || []).length === 0
          ? (!loadingPool && <div className="text-muted p-2" data-testid="empty-state">{editableTranslate('global.no_data', 'Keine Daten')}</div>)
          : (employees || []).map(employee => {
            const isChecked = instanceChecked.includes(employee.hash_id);
            const values = instanceValues[employee.hash_id] || {};
            return (
              <div data-testid={`employee-instance-${employee.hash_id}`} key={employee.hash_id} className="border-bottom py-2">
                <div className="form-check">
                  <label className="form-check-label">
                    <input className="form-check-input" type="checkbox" checked={isChecked} onChange={e => toggleInstanceRow(employee.hash_id, e.target.checked)} data-testid={`employee-instance-check-${employee.hash_id}`} />
                    <span className="ml-1">
                      {`${employee.first_name || ''} ${employee.last_name || ''}`.trim()}
                      {employee.employee_number ? <span className="text-muted ml-1">({employee.employee_number})</span> : null}
                    </span>
                  </label>
                </div>
                {
                  isChecked && (
                    <div className="p-3 bg-light rounded mb-2">
                      {
                        canUseReferenceMode && <div className="row">
                          <div className="col-xl-4 col-lg-6 form-group">
                            <Field label={fieldLabels.rating_mode}>
                              <NativeSelect data-testid="rating-mode-select" onChange={e => setInstanceValue(employee.hash_id, 'rating_mode', e.target.value)}>
                                <option value={NORMAL_RATING_LOCKED}>
                                  {translate('field.rating_mode.normal_rating_locked', 'Normale Bewertung')}
                                </option>
                                <option value={NORMAL_RATING}>
                                  {translate('field.rating_mode.normal_rating', 'Bewertung')}
                                </option>
                              </NativeSelect>
                            </Field>
                          </div>
                        </div>
                      }
                      <div className="row">
                        {formKeys.map((key) => (
                          <div key={key} className="col-xl-4 col-lg-6 form-group">
                            <Field
                              label={fieldLabels[key]}
                              htmlFor={`create-scorecard-instance-${employee.hash_id}-${key}`}
                              help={(canUseReferenceMode && instanceValues?.[employee.hash_id]?.rating_mode === NORMAL_RATING_LOCKED) && <i className="bi bi-info-circle ml-1" data-tooltip-id={`action-detail-first-${key}-tooltip`}></i>}
                            >
                              {
                                (canUseReferenceMode && instanceValues?.[employee.hash_id]?.rating_mode === NORMAL_RATING_LOCKED) ?
                                  <Input data-testid={`create-scorecard-instance-${key}-input`} id={`create-scorecard-instance-${employee.hash_id}-${key}`} defaultValue={'...'} disabled={true} /> :
                                  <div style={{ display: 'contents' }} data-testid={`create-scorecard-instance-${key}-select`}>
                                    <Select
                                      inputId={`create-scorecard-instance-${employee.hash_id}-${key}`}
                                      options={selectOptionsForKey(key, values)}
                                      value={selectOptionsForKey(key, values).find(o => o.value === values[key]) || null}
                                      onChange={option => setInstanceValue(employee.hash_id, key, option ? option.value : '')}
                                      isClearable
                                      placeholder={translate('form.select_placeholder')}
                                    />
                                  </div>
                              }
                            </Field>
                          </div>
                        ))}
                        {
                          isDbModuleActive && <div className="col-xl-4 col-lg-6 form-group">
                            <Field label={fieldLabels['group']} htmlFor={`create-scorecard-instance-${employee.hash_id}-group`} help={<i className="bi bi-info-circle ml-1" data-tooltip-id="action-detail-state-group-tooltip"></i>}>
                              <Input data-testid="create-scorecard-instance-group-input" id={`create-scorecard-instance-${employee.hash_id}-group`} defaultValue={'...'} disabled={true} />
                            </Field>
                          </div>
                        }
                      </div>
                    </div>
                  )
                }
              </div>
            );
          })
      }

      <ListPagination
        page={pagination.currentPage}
        perPage={pagination.perPage}
        totalPages={pagination.totalPages}
        totalRows={pagination.total}
        setPage={setPoolPage}
        setPerPage={handlePoolPerPageChange}
      />
    </div>
  );

  const getChosenValues = () => {
    const data = getValues();
    const arr = [];
    if (canUseReferenceMode) {
      arr.push({ label: fieldLabels.rating_mode, value: translate(`field.rating_mode.${data?.rating_mode}`) });
      if (data?.rating_mode === NORMAL_RATING_LOCKED) return arr;
    }
    return arr.concat(formKeys.map((key) => {
      const item = optionsForKey(key).find(o => o.hash_id === data[key]);
      return { label: fieldLabels[key], value: item?.name || '—' };
    }));
  };

  const getInstanceChosenValues = (hash) => {
    const values = instanceValues[hash] || {};
    const arr = [];
    if (canUseReferenceMode) {
      arr.push({ label: fieldLabels.rating_mode, value: translate(`field.rating_mode.${values?.rating_mode}`) });
    }
    return arr.concat(formKeys.map((key) => {
      const item = optionsForKey(key, values).find(o => o.hash_id === values[key]);
      return { label: fieldLabels[key], value: item?.name || '—' };
    }));
  };

  return (
    <div className="mt-3">
      {!asSubPage && <hr className="mt-2 mb-4" />}

      {
        error &&
        <div className="mb-3">
          <ErrorAlert error={error} onClose={() => { setError(null); setErrorType('danger'); }} isHtml={true} type={errorType} />
        </div>
      }

      {
        page === 1 && <>
          {
            actionMode === 'overwrite' && <div className="mb-5">
              <div className="font-weight-bold">{editableTranslate('action.detail.employees', 'Beschäftigte')}</div>
              <hr className="my-2" />
              <div className="mb-3">{editableTranslate('action.detail.select_employees', 'Wählen Sie die Beschäftigten aus, auf die die Änderungen angewendet werden sollen.')}</div>
              {
                !poolLoaded
                  ? <div className="text-center p-4" data-testid="loading">
                    <div className="spinner-border" role="status"><span className="sr-only">Wird geladen...</span></div>
                  </div>
                  : <EmployeeSelector employees={employees || []} selected={selected} setSelected={setSelected} onSelectionChange={(emps, checked) => setSelectedEmployees(prev => applySelectionChange(prev, emps, checked))} {...poolPaginationProps} />
              }
            </div>
          }

          <div className="mb-3">
            <div className="action-tab-mode-div">
              <div className="flex-1 border-bottom">
                <div className="employee-management-tabs">
                  {
                    // When embedded in the system-filter form, the parent supplies the
                    // full tab set (and controls selection) so the user can switch to the
                    // sibling action types. Standalone (suggested action) it renders its
                    // own single tab.
                    (tabs || [{
                      key: 'create_balanced_scorecard',
                      icon: 'bi-person-square',
                      label: isDbModuleActive
                        ? editableTranslate('action.detail.create_evaluation', 'Beurteilung erstellen')
                        : editableTranslate('action.detail.create_balanced_scorecard', 'Leistungskarte anlegen'),
                    }]).map((tab) => (
                      <div data-testid={`action-tab-${tab.key}`}
                        key={tab.key}
                        className={`cursor-pointer ${(activeTab ?? actionType) === tab.key ? 'active' : ''}`}
                        onClick={() => (onTabChange || setActionType)(tab.key)}
                      >
                        <i className={`bi ${tab.icon} mr-1`}></i>
                        <span>{tab.label}</span>
                      </div>
                    ))
                  }
                </div>
              </div>
              <div className="btn-group btn-group-switch-mode normal-border padding-normal" role="group">
                <button type="button" className={`btn ${actionMode === 'overwrite' ? ' active' : ''}`} onClick={() => changeActionMode('overwrite')} data-testid="mode-overwrite-button">
                  {editableTranslate('action.detail.mode.overwrite', 'Überschreiben')}
                </button>
                <button type="button" className={`btn ${actionMode === 'per_instance' ? ' active' : ''}`} onClick={() => changeActionMode('per_instance')} data-testid="mode-per-instance-button">
                  {editableTranslate('action.detail.mode.per_instance', 'Pro Beschäftigte ausfüllen')}
                </button>
              </div>
            </div>
          </div>

          {
            actionMode === 'overwrite' ? <>
              {overwriteForm}
            </> : renderInstanceEditor()
          }

          <ReactTooltip id={`action-detail-state-group-tooltip`} content={editableTranslate(`action.detail.state_group_tooltip`)} place="bottom" clickable={true} />

          {formKeys.map((key) => <ReactTooltip key={`action-detail-first-${key}-tooltip`} id={`action-detail-first-${key}-tooltip`} content={editableTranslate(`action.detail.first_${key}_tooltip`)} place="bottom" clickable={true} />)}

          <div className="settings-sticky-footer mt-3">
            {onBack
              ? <Button variant="secondary" onClick={onBack} data-testid="back-button" className="mr-2">
                {editableTranslate('button.back', 'Zurück')}
              </Button>
              : <Button data-testid="back-button" as={Link} variant="secondary" className="mr-2" to="/settings/actions">
                {editableTranslate('button.back', 'Zurück')}
              </Button>}

            <Button variant="primary" onClick={goToPreview} disabled={!poolLoaded || ((actionMode === 'overwrite' && selected.length === 0) || (actionMode === 'per_instance' && instanceChecked.length === 0))} data-testid="next-button">
              {editableTranslate('button.next', 'Weiter')}
            </Button>
          </div>
        </>
      }

      {
        page === 2 && <>
          <div>
            {
              actionMode === 'overwrite'
                ? <ActionChangePreview whatItems={getChosenValues()} whoList={buildWhoList(selected, selectedEmployees)} actionType={actionType} />
                : <ActionChangePreview perInstance whoList={pairInstanceValues(instanceChecked, checkedEmployees, getInstanceChosenValues)} actionType={actionType} />
            }
          </div>

          <div className="settings-sticky-footer d-flex mt-3">
            <Button variant="secondary" onClick={() => setPage(1)} disabled={loadingPreview} data-testid="back-button" className="mr-2">
              {editableTranslate('button.back', 'Zurück')}
            </Button>
            <Button variant="primary" onClick={goToApply} loading={loadingPreview} data-testid="proceed-button">{editableTranslate('button.proceed', 'Fortfahren')}</Button>
          </div>
        </>
      }

      {
        page === 3 && <>
          <div>
            <div className="font-weight-bold">{editableTranslate('action.detail.summary', 'Zusammenfassung')}</div>
            <hr className="mt-2" />
            {
              preview && <>
                <p className="mb-2" dangerouslySetInnerHTML={{
                  __html: editableTranslateString(
                    isDbModuleActive ? 'action.detail.summary_evaluation' : 'action.detail.summary_balanced_scorecard',
                    isDbModuleActive ? 'Für $X Beschäftigte wird eine Beurteilung erstellt.' : 'Für $X Beschäftigte wird eine Leistungskarte erstellt.'
                  ).replace('$X', preview.affected_count ?? 0)
                }} />
                {
                  buildSkippedWarning(preview) && <div className="alert alert-warning mt-3" data-testid="skipped-warning">
                    <div className="mb-2" dangerouslySetInnerHTML={{ __html: sanitize(buildSkippedWarning(preview), { ADD_ATTR: ["target", "rel"] }) }} />
                  </div>
                }
              </>
            }
          </div>

          <div className="settings-sticky-footer d-flex mt-3">
            <Button variant="secondary" onClick={() => setPage(2)} disabled={submitting} data-testid="back-button" className="mr-2">
              {editableTranslate('button.back', 'Zurück')}
            </Button>
            <Button variant="primary" onClick={handleApply} loading={submitting} disabled={(preview?.affected_count ?? 0) === 0} data-testid="apply-button">{editableTranslate('button.apply', 'Anwenden')}</Button>
          </div>
        </>
      }
    </div>
  );
}
